import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  getInstructionEngine,
  getLocalPreference,
  resetInstructionPreference,
  saveLocalPreference,
  updateInstructionPreference,
} from '../services/pageInstructionService';
import type {
  GetInstructionEngineRequest,
  PageInstructionEngineResponseDto,
  PageRouteKey,
  UpdateInstructionPreferenceRequest,
} from '../types/pageInstruction';

interface UseInstructionEngineOptions {
  pageRouteKey: PageRouteKey | string;
  programId?: string | null;
  roleId?: string | null;
  activeContextKeys?: string[];
  enabled?: boolean;
}

interface UseInstructionEngineResult {
  data: PageInstructionEngineResponseDto | null;
  shouldShow: boolean;
  isExpanded: boolean;
  isDismissed: boolean;
  isFirstVisit: boolean;
  isLoading: boolean;
  error: Error | null;
  toggleCollapsed: () => void;
  collapse: () => void;
  expand: () => void;
  dontShowAgain: () => void;
  reset: () => void;
  refetch: () => void;
}

export function useInstructionEngine(options: UseInstructionEngineOptions): UseInstructionEngineResult {
  const {
    pageRouteKey,
    programId,
    roleId,
    activeContextKeys = [],
    enabled = true,
  } = options;
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const initialLocalPreference = getLocalPreference(pageRouteKey, programId);

  const [localIsCollapsed, setLocalIsCollapsed] = useState<boolean>(() => {
    return initialLocalPreference?.isCollapsed ?? true;
  });
  const [localDontShow, setLocalDontShow] = useState<boolean>(() => {
    return initialLocalPreference?.dontShowAgain ?? false;
  });

  const normalizedContextKey = useMemo(() => {
    const normalized = activeContextKeys
      .map((contextKey) => contextKey.trim())
      .filter(Boolean)
      .sort();

    return normalized.join('|');
  }, [activeContextKeys]);

  const queryKey = ['instructionEngine', pageRouteKey, programId ?? null, roleId ?? null, normalizedContextKey];

  const { data, isLoading, error, refetch } = useQuery<PageInstructionEngineResponseDto | null, Error>({
    queryKey,
    queryFn: async () => {
      try {
        const request: GetInstructionEngineRequest = {
          pageRouteKey,
          programId: programId ?? undefined,
          roleId: roleId ?? undefined,
          activeContextKeys,
        };

        return await getInstructionEngine(request);
      } catch (err) {
        console.warn('Failed to resolve instruction engine, using local preference:', err);
        const localPref = getLocalPreference(pageRouteKey, programId);
        return {
          instructions: [],
          primaryInstruction: null,
          preference: {
            preferenceId: null,
            pageRouteKey,
            programId: programId ?? null,
            isCollapsed: localPref?.isCollapsed ?? true,
            dontShowAgain: localPref?.dontShowAgain ?? false,
            isFirstVisit: !localPref,
            viewCount: localPref?.viewCount ?? 0,
          },
          hasInstruction: false,
        };
      }
    },
    enabled: enabled && !!user,
    staleTime: 10 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const updateMutation = useMutation({
    mutationFn: async (request: UpdateInstructionPreferenceRequest) => {
      try {
        await updateInstructionPreference(request);
      } catch {
        saveLocalPreference(
          pageRouteKey,
          {
            isCollapsed: request.isCollapsed,
            dontShowAgain: request.dontShowAgain,
          },
          programId,
        );
      }
    },
    onMutate: async (request) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<PageInstructionEngineResponseDto>(queryKey);

      if (previous) {
        queryClient.setQueryData<PageInstructionEngineResponseDto>(queryKey, {
          ...previous,
          preference: {
            ...previous.preference,
            ...(request.isCollapsed !== undefined && { isCollapsed: request.isCollapsed }),
            ...(request.dontShowAgain !== undefined && { dontShowAgain: request.dontShowAgain }),
          },
        });
      }

      return { previous };
    },
    onError: (_, __, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
  });

  const resetMutation = useMutation({
    mutationFn: async () => {
      await resetInstructionPreference(pageRouteKey, programId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const shouldShow = useMemo(() => {
    if (localDontShow) return false;
    if (!data) return false;
    if (data.preference.dontShowAgain) return false;
    return data.hasInstruction;
  }, [data, localDontShow]);

  const isExpanded = !localIsCollapsed;
  const isFirstVisit = useMemo(() => data?.preference.isFirstVisit ?? true, [data]);

  useEffect(() => {
    if (!data || initialLocalPreference) {
      return;
    }

    setLocalIsCollapsed(data.preference.isFirstVisit ? true : data.preference.isCollapsed);
    setLocalDontShow(data.preference.dontShowAgain);
  }, [data, initialLocalPreference]);

  const toggleCollapsed = useCallback(() => {
    const next = !localIsCollapsed;
    setLocalIsCollapsed(next);
    saveLocalPreference(pageRouteKey, { isCollapsed: next, dontShowAgain: localDontShow }, programId);
    updateMutation.mutate({ pageRouteKey, programId, isCollapsed: next });
  }, [localDontShow, localIsCollapsed, pageRouteKey, programId, updateMutation]);

  const collapse = useCallback(() => {
    setLocalIsCollapsed(true);
    saveLocalPreference(pageRouteKey, { isCollapsed: true, dontShowAgain: localDontShow }, programId);
    updateMutation.mutate({ pageRouteKey, programId, isCollapsed: true });
  }, [localDontShow, pageRouteKey, programId, updateMutation]);

  const expand = useCallback(() => {
    setLocalIsCollapsed(false);
    saveLocalPreference(pageRouteKey, { isCollapsed: false, dontShowAgain: localDontShow }, programId);
    updateMutation.mutate({ pageRouteKey, programId, isCollapsed: false });
  }, [localDontShow, pageRouteKey, programId, updateMutation]);

  const dontShowAgainHandler = useCallback(() => {
    setLocalDontShow(true);
    saveLocalPreference(pageRouteKey, { isCollapsed: localIsCollapsed, dontShowAgain: true }, programId);
    updateMutation.mutate({ pageRouteKey, programId, dontShowAgain: true });
  }, [localIsCollapsed, pageRouteKey, programId, updateMutation]);

  const reset = useCallback(() => {
    resetMutation.mutate();
  }, [resetMutation]);

  return {
    data: data ?? null,
    shouldShow,
    isExpanded,
    isDismissed: localDontShow,
    isFirstVisit,
    isLoading,
    error: error ?? null,
    toggleCollapsed,
    collapse,
    expand,
    dontShowAgain: dontShowAgainHandler,
    reset,
    refetch: () => {
      void refetch();
    },
  };
}